import React from 'react';

/**
 * Styling options that can be applied to table cells, columns, rows, and the table itself.
 * Individual cell styles take precedence over rows, rows take precedence over columns, and columns precedence over the table.
 */
export interface TableStyles {
  /** Determines how content should align vertically. */
  align?: 'middle' | 'top';

  /** Determines which side a border should be applied. */
  border?: 'both' | 'bottom' | 'none' | 'right';

  /** Determines the background color applied. */
  color?: 'gray-15' | 'gray-60' | 'purple-10' | 'purple-60' | 'teal-10' | 'transparent' | 'white';
}

/** Interface used to generate `<td>` and `<th>` elements. */
export interface TableCellData {
  /** Child elements to render within the table cell. */
  children?: React.ReactNode | React.ReactNode[];

  /** Custom classname of the table cell. */
  className?: string;

  /**
   * Specifies the number of columns that the table cell should span.
   * @defaultValue `1`
   */
  colSpan?: number;

  /**
   * Contains a list of space-separated strings, each corresponding to the id attribute
   * of the `<th>` elements that provide headings for this table cell.
   */
  headers?: string;

  /**
   * Unique identifier of the table cell.
   * If provided, the `id` must be unique (per page) for accessibility purposes.
   */
  id?: string;

  /**
   * Determines if the table cell should render as a `<th>` element.
   * Otherwise renders as a `<td>` element.
   * @defaultValue `false`
   */
  isHeader?: boolean;

  /**
   * Specifies the number of rows that the table cell should span.
   * @defaultValue `1`
   */
  rowSpan?: number;

  /**
   * Determines the scope of the table cell header.
   * Only applies if the `isHeader` property is `true`.
   */
  scope?: 'col' | 'row';

  /** Cell styling options. */
  styles?: TableStyles;
}

/** Interface used to generate `<col>` elements. */
export interface TableColumnData {
  /** Custom classname of the table column. */
  className?: string;

  /**
   * Specifies the number of columns that the element should span.
   * @defaultValue `1`
   */
  span?: number;

  /** Column styling options. */
  styles?: TableStyles;
}

/** Interface used to generate `<tr>` elements. */
export interface TableRowData {
  /**
   * Properties for each table cell within the table row.
   * @defaultValue `[]`
   */
  cellData: TableCellData[];

  /** Custom classname of the table row. */
  className?: string;

  /**
   * Determines if the row should render within the `<tbody>`, `<thead>`, or `<tfoot>` elements.
   * If `header` is selected, all cells within the `cellData` array will be rendered as `<th>` elements by default.
   * @defaultValue `'body'`
   */
  placement?: 'body' | 'footer' | 'header';

  /** Row styling options. */
  styles?: TableStyles;
}
